import clsx from "clsx";
import React, { useEffect } from "react";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import AvatarStack from "components/common/AvatarStack";
import IconButton from "components/common/IconButton";
import { usePinSpace, useUnpinSpace } from "hooks/spaceHooks";
import CustomToast from "components/common/CustomToast";
import { getFromStorage, removeFromStorage, saveToStorage } from "helpers";
import * as UrlConstants from "constants/urls";
import { useMergedSearchParams } from "helpers/mergeSearchParams";

type SpaceListItemProps = {
    id: string;
    title: string;
    description: string;
    isStarred?: boolean;
    collaborators?: {
        name: string;
        image: string;
    }[];
    color?: string;
    numberOfCOnversations?: number;
    active?: boolean;
};

const SYNTH_SPACE_ID = "synth-space-id";
export default function SpaceListItem(props: SpaceListItemProps) {
    const {
        id,
        title,
        description,
        isStarred = false,
        collaborators = [],
        numberOfCOnversations = 0,
        color,
        active,
    } = props;
    const navigate = useNavigate();

    const { spaceId } = useParams();

    const isGuest = !!getFromStorage("ally-user")?.isGuest;
    const [pinSpaceMutation, pinSpaceMutationResult] = usePinSpace();
    const [unpinSpaceMutation, unpinSpaceMutationResult] = useUnpinSpace();
    const handleChangeStarred = (id: string, action: "pin" | "unpin") => {
        try {
            if (action === "pin") {
                pinSpaceMutation(id);
            } else {
                unpinSpaceMutation(id);
            }
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    message={
                        action === "pin"
                            ? "Space pinned successfully"
                            : "Space unpinned successfully"
                    }
                    type="success"
                />
            ));
        } catch (error) {
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    message={action === "pin" ? "Space pinned failed" : "Space unpinned failed"}
                    type="error"
                />
            ));
        }
    };

    const handleChangeSpace = () => {
        navigate(
            isGuest
                ? generatePath(UrlConstants.SPACES_GUEST_DETAILS, { spaceId: id })
                : generatePath(UrlConstants.SPACES_DETAILS, { spaceId: id })
        );
        saveToStorage(SYNTH_SPACE_ID, { spaceId: id });
    };

    useEffect(() => {
        saveToStorage(SYNTH_SPACE_ID, { spaceId });

        return () => {
            removeFromStorage(SYNTH_SPACE_ID);
        };
    }, [spaceId]);

    return (
        <div
            className={clsx(
                active && "bg-slate-50",
                "flex items-start gap-2 p-[12px] hover:bg-slate-50 cursor-pointer rounded-lg transition-colors w-full"
            )}
            onClick={handleChangeSpace}
        >
            <div>
                <IconButton
                    variant="soft"
                    color="primary"
                    style={{ backgroundColor: `${color}1a`, color: color }}
                >
                    <i className="ri-question-answer-line"></i>
                </IconButton>
            </div>

            <div className="overflow-hidden w-full">
                <div className="overflow-hidden">
                    <div className="flex gap-1 justify-between">
                        {" "}
                        <p className="text-slate-800 font-rocGroteskMedium text-[14px] font-semibold truncate whitespace-nowrap text-ellipsis">
                            {title}
                        </p>
                        <div>
                            {/* Hidden: Unread count badge
                            {numberOfCOnversations > 0 && (
                                <div className="bg-star-gold font-rocGroteskMedium  p-[4px] text-black text-[12px] rounded-md leading-[16px]">
                                    {numberOfCOnversations}
                                </div>
                            )}
                            */}
                        </div>
                    </div>

                    <p className="text-slate-500 font-rocGroteskRegular text-[13px] line-clamp-2">
                        {description}
                    </p>
                </div>

                <div className="flex justify-between gap-1 mt-[8px] w-full">
                    <AvatarStack
                        images={collaborators.map((collaborator) => ({
                            src: collaborator.image,
                            alt: collaborator.name,
                        }))}
                        max={4}
                    />

                    <button
                        onClick={() => handleChangeStarred(id, isStarred ? "unpin" : "pin")}
                        disabled={
                            pinSpaceMutationResult.isLoading || unpinSpaceMutationResult.isLoading
                        }
                    >
                        <i
                            className={clsx(
                                "ri-star-fill",
                                isStarred ? "text-star-gold" : "text-slate-200"
                            )}
                        ></i>
                    </button>
                </div>
            </div>
        </div>
    );
}
